import { Accordion, Box, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Layout from "../../components/app/Layout";
import BaseContainer from "../../components/form/BaseContainer";
import { IntegrationService } from "../../service/integration.service";
import { useParams } from "react-router-dom";
import BasePageTitleContainer from "../../components/form/BasePageTitleContainer";
import { RootState } from "../../store";
import { successToast } from "../../constants/toast.constants";
import { useSelector } from "react-redux";
import { Loading } from "../../components/shared/Loading";

import { RunAttemptsDurationCard } from "../../components/history/card/RunAttemptsDuration";
import { RunAttemptsSuccessCard } from "../../components/history/card/RunAttemptsSuccess";
import { TotalDurationCard } from "../../components/history/card/TotalDuration";
import { AverageDurationCard } from "../../components/history/card/AverageDuration";
import { RunAttemptsHistoryCard } from "../../components/history/card/RunAttemptsHistory";

const RUN_DEVIATION_THRESHOLD = 0.5;

const baseContainerStyles = {
  mx: "auto",
  padding: "0",
  flexGrow: "1",
  borderRadius: "2px",
  minWidth: "95%",
  minH: { base: "calc(100vh - 80px - 24px)" },
  paddingBottom: "50px",
  position: "relative",
};

const integrationService = new IntegrationService();

export const History: React.FC<any> = (props: any) => {
  const { id } = useParams();

  const toast = useToast();

  const [runs, setRuns] = useState([] as any[]);
  const [loading, setLoading] = useState(false);
  
  const [metric, setMetrics] = useState({
    totalDuration: 0,
    averageDuration: 0,
  } as any);

  const [focussedRun, setFocussedRun] = useState({
    id: null,
    status: null,
    logs: [],
  });

  const integrationWorkflows = useSelector(
    (state: RootState) => state.workflows
  );

  const init = () => {
    setWorkflowData();
  };

  const filteredWorkflow = integrationWorkflows.integrationItems.filter(
    (item) => {
      const numericID = parseInt(id);
      return item.id === numericID;
    }
  );

  const setWorkflowData = () => {
    setRuns(filteredWorkflow);
    setMetrics({
      totalDuration: filteredWorkflow?.reduce(
        (acc, curr) => acc + curr.duration,
        0
      ),
      averageDuration: Math.round(
        filteredWorkflow.reduce((acc, curr) => acc + curr.duration, 0) /
          (filteredWorkflow.length || 1)
      ),
    });
  };

  const handleToggle = async (runId: string) => {
    if (focussedRun.id === runId) {
      setFocussedRun({ id: null, status: null, logs: [] });
      return;
    }

    setFocussedRun((prev) => ({
      ...prev,
      id: runId === prev.id ? null : runId,
    }));

    await setRunHistory(runId);
  };

  const setRunHistory = async (runId: string) => {
    setLoading(true);
    const run = await integrationService.getWorkflowRunLog({
      integrationName: "RWG-Daily",
      workflowId: id,
      workflowRunId: runId,
    });

    const uniqueMessages = run?.logs?.reduce((acc, x) => {
      if (!acc.find((y) => y.Message.S === x.Message.S)) {
        acc.push(x);
      }
      return acc;
    }, []);

    setFocussedRun({ id: runId, status: run?.status, logs: uniqueMessages });

    if (run?.status === "Completed") {
      await setWorkflowData();
    }

    setLoading(false);
  };

  useEffect(() => {
    init();
    handleToggle(id);
  }, [id]);

  return (
    <Layout>
      <Loading loading={loading} />
      <BasePageTitleContainer
        style={{
          background: "transparent",
          border: "none",
          maxWidth: "95%",
          padding: "0px",
        }}
      >
        <div className="my-5 animate-fade-in">
          <div className="label-caps mb-2">History</div>
          <div className="text-3xl font-medium tracking-tight text-foreground">
            {runs[0]?.friendlyName || "Workflow"}
          </div>
        </div>
      </BasePageTitleContainer>
      <Box className="flex flex-row w-[95%] h-[360px] mx-auto gap-6">
        <RunAttemptsDurationCard runs={runs} />
        <RunAttemptsSuccessCard runs={runs} metric={metric} />
        <Box className="flex flex-col max-w-[55%] flex-grow">
          <TotalDurationCard metric={metric} runCount={runs.length} />
          <AverageDurationCard metric={metric} runCount={runs.length} />
        </Box>
      </Box>
      <BaseContainer styles={baseContainerStyles}>
        <Accordion allowToggle>
          {runs.length > 0 ? (
            runs.map((run: any, i: number) => (
              <RunAttemptsHistoryCard
                key={i}
                run={run}
                focussedRun={focussedRun}
                handleToggle={handleToggle}
                setRunHistory={setRunHistory}
                metric={metric}
              />
            ))
          ) : (
            <Box className="p-8">
              <Box className="font-semibold text-xl">
                Sorry. No run attempts found for this workflow.
              </Box>
            </Box>
          )}
        </Accordion>
        <Box fontSize="14px" position="absolute" p="16px" bottom="0">
          Displaying top {runs.length} results.
        </Box>
      </BaseContainer>
    </Layout>
  );
};
